import {
  getPcidFromJwt
} from "./chunk-HUBXNHHE.mjs";
import {
  generateCodeChallenge,
  generateNonce,
  generateScope,
  generateStateObj
} from "./chunk-CO4RI5BX.mjs";
import {
  validateSessionCsrf,
  validateSessionNonce,
  validateToken
} from "./chunk-5YZBVQLS.mjs";
import {
  LogManager
} from "./chunk-BEVY2FRL.mjs";
import {
  AuthCodeNotFoundError,
  FetchTokenResponseError,
  GetCheckMigratedFlagResponseError,
  GetProfileResponseError,
  GetRefreshTokenResponseError,
  InvalidAuthCodeError,
  JwtValidationError,
  SetRefreshTokenResponseError
} from "./chunk-RDVYLKWU.mjs";
import {
  errorMessages
} from "./chunk-KSIEOKOR.mjs";
import {
  getApiBaseUrl,
  getApiUrl,
  getAudienceUrl,
  getBaseUrl
} from "./chunk-XEDCQDS7.mjs";

// src/services/session-impl.ts
import { stringify } from "qs";
import { generate } from "randomized-string";
var SessionImpl = class _SessionImpl {
  constructor(environment, rp) {
    // Buffer to manage access token fetching
    this.accessTokenPromise = null;
    this._accessToken = "";
    this.environment = environment;
    this.relyingParty = rp;
    let correlationId = null;
    if (typeof sessionStorage !== "undefined") {
      correlationId = sessionStorage.getItem("pcidCorrelationId" /* PcidCorrelationId */);
    }
    if (!correlationId) {
      correlationId = generate(10);
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("pcidCorrelationId" /* PcidCorrelationId */, correlationId);
      }
    }
    this.logManager = new LogManager(environment, correlationId, rp);
  }
  // Singleton instance
  static getInstance(environment, rp) {
    if (!_SessionImpl.instance) {
      _SessionImpl.instance = new _SessionImpl(environment, rp);
    }
    return _SessionImpl.instance;
  }
  setLogDetails(logDetails) {
    this.logManager.setLogDetails(logDetails);
  }
  /**
   * This function checks if this is the first run.
   * @returns true if this is the first run, false otherwise
   */
  async isFirstRun() {
    const path = `${getApiBaseUrl()}/api/check-migrated-flag`;
    const response = await fetch(path, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new GetCheckMigratedFlagResponseError(
        `Invalid JSON response received.`
      );
    }
    void this.logManager.log(
      "INFO" /* INFO */,
      `isFirstRun flag: ${data.isFirstRun}`
    );
    return data.isFirstRun;
  }
  /**
   * This function is for importing existing tokens to this SDK.
   * @param accessToken - The access token from the token provider
   * @param idToken - The id token from the token provider
   * @param refreshToken - The refresh token from the token provider
   * @returns null
   */
  async importTokens(importedAccessToken, importedIdToken, importedRefreshToken) {
    let storedAccessToken, storedIdToken;
    if (typeof localStorage !== "undefined") {
      storedAccessToken = localStorage.getItem("pcid_at" /* PcidAccessToken */);
      storedIdToken = localStorage.getItem("pcid_it" /* PcidIdToken */);
    }
    const response = await fetch(
      `${getApiBaseUrl()}/api/get-refresh-token`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      }
    );
    let data;
    try {
      data = await response.json();
    } catch (error) {
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `Error while getting refresh token: ${error.message}`
      );
      throw new GetRefreshTokenResponseError(error.message);
    }
    const storedRefreshToken = data.refreshToken;
    void this.logManager.log("INFO" /* INFO */, "Tokens retreived");
    if (storedAccessToken || storedIdToken || storedRefreshToken) {
      void this.logManager.log(
        "INFO" /* INFO */,
        `tokens exist. skipping import now.`
      );
      return null;
    }
    if (typeof localStorage !== "undefined") {
      void this.logManager.log(
        "INFO" /* INFO */,
        "No existing tokens. Migrating tokens to SDK now."
      );
      localStorage.setItem("pcid_at" /* PcidAccessToken */, importedAccessToken);
      localStorage.setItem("pcid_it" /* PcidIdToken */, importedIdToken);
    }
    let pcidUuid = null;
    try {
      if (importedIdToken) {
        pcidUuid = getPcidFromJwt(importedIdToken);
      } else if (importedAccessToken) {
        pcidUuid = getPcidFromJwt(importedAccessToken);
      }
      if (pcidUuid) {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("pcid_uuid" /* PcidUuid */, pcidUuid);
        }
        void this.logManager.log(
          "INFO" /* INFO */,
          `importTokens: PCid UUID extracted and stored: ${pcidUuid}`
        );
      } else {
        void this.logManager.log(
          "WARN" /* WARN */,
          "importTokens: PCid UUID could not be extracted from imported tokens"
        );
      }
    } catch (error) {
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `importTokens: Error extracting PCid UUID: ${error.message}`
      );
    }
    try {
      await fetch(`${getApiBaseUrl()}/api/set-refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          refreshToken: importedRefreshToken
        })
      });
    } catch (error) {
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `Error while setting refresh token: ${error.message}`
      );
      throw new SetRefreshTokenResponseError(error.message);
    }
    void this.logManager.log("INFO" /* INFO */, "Tokens migrated successfully.");
    this._accessToken = importedAccessToken;
    return null;
  }
  /**
   * This function will create all the necessary challenge pieces (eg. nonce, code_challenge) and
   * build a URL to create login intent with the token provider. It will generate an encoded state
   * based on the passed in arguments. It will include the scope required for access to this app.
   *
   * @param payload - a payload object containing language, path and possibly message strings
   * @returns The generated URL
   */
  async generateLoginUrl(payload) {
    const codeChallenge = await generateCodeChallenge();
    const nonce = generateNonce();
    const encodedState = generateStateObj(payload);
    const query = {
      client_id: payload.clientId,
      response_type: "code",
      redirect_uri: payload.redirectUrl,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
      scope: generateScope(payload.addOfflineAccessToScopes, payload.scope),
      nonce,
      state: encodedState
    };
    const queryString = stringify(query, { encode: false });
    const url = `${getAudienceUrl(
      this.environment
    )}/oauth2/v1/authorize?${queryString}`;
    void this.logManager.log("INFO" /* INFO */, `Login url generated: ${url}`);
    return url;
  }
  /**
   * Fetches session tokens by using the code received.
   * @param code - The Auth code received after login
   * @param state - The state received after login
   * @returns accessToken
   */
  async getTokens(code, state) {
    void this.logManager.log(
      "INFO" /* INFO */,
      `Login Redirect url params: code=${code} state=${state}`
    );
    validateSessionCsrf(state, this.logManager);
    const codeVerifier = sessionStorage.getItem("pcidCodeVerifierKey" /* PcidCodeVerifierKey */);
    if (!codeVerifier) {
      throw new AuthCodeNotFoundError(
        errorMessages.AUTH_CODE_VERIFIER_KEY_NOT_FOUND
      );
    }
    const requestBody = {
      code,
      codeVerifier,
      relyingParty: this.relyingParty
    };
    const path = `${getApiBaseUrl()}/api/token`;
    const response = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    });
    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new FetchTokenResponseError(`Invalid JSON response received.`);
    }
    if (data.error) {
      if (data.error === "invalid_grant" && data.error_description === errorMessages.INVALID_AUTH_CODE) {
        void this.logManager.log(
          "ERROR" /* ERROR */,
          `Get tokens failed because of Invalid Code verifier in token request`
        );
        throw new InvalidAuthCodeError(errorMessages.INVALID_AUTH_CODE);
      }
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `Get tokens failed: ${data.error_description}`
      );
      throw new FetchTokenResponseError(
        data.error_description || "Error while fetching tokens"
      );
    }
    if (data.id_token) {
      validateSessionNonce(data.id_token, this.logManager);
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("pcid_it" /* PcidIdToken */, data.id_token);
      }
    }
    if (data.access_token) {
      if (!await validateToken(
        data.access_token,
        this.environment,
        this.logManager
      )) {
        throw new JwtValidationError(errorMessages.JWT_VALIDATION_FAILED);
      }
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("pcid_at" /* PcidAccessToken */, data.access_token);
      }
      this._accessToken = data.access_token;
    }
    let pcidUuid = null;
    try {
      if (data.id_token) {
        pcidUuid = getPcidFromJwt(data.id_token);
      } else if (data.access_token) {
        pcidUuid = getPcidFromJwt(data.access_token);
      }
      if (pcidUuid) {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("pcid_uuid" /* PcidUuid */, pcidUuid);
        }
        void this.logManager.log(
          "INFO" /* INFO */,
          `getTokens: PCid UUID extracted and stored: ${pcidUuid}`
        );
      } else {
        void this.logManager.log(
          "WARN" /* WARN */,
          "getTokens: PCid UUID could not be extracted from tokens"
        );
      }
    } catch (error) {
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `getTokens: Error extracting PCid UUID: ${error.message}`
      );
    }
    this.cleanup();
    void this.logManager.log("INFO" /* INFO */, `Tokens returned`);
    return data.access_token;
  }
  /**
   * This function will clear the session storage of the nonce and code verifier.
   */
  cleanup() {
    sessionStorage.removeItem("pcidNonceKey" /* PcidNonceKey */);
    sessionStorage.removeItem("pcidCodeVerifierKey" /* PcidCodeVerifierKey */);
    sessionStorage.removeItem("csrfKey" /* PcidCsrfKey */);
  }
  /**
   * Retrieves the access token from memory if available,
   * otherwise fetches it from local storage.
   * If not available in local storage, tries to refresh the token.
   * If the token is not available, returns an empty string.
   *
   * @param shouldRefresh - If true, tries to refresh the token. Default is false.
   *
   * @returns a Promise of access token stored in memory or in local storage.
   * Returns empty string if access token is not available
   */
  async getAccessToken(shouldRefresh = false) {
    if (this._accessToken && this._accessToken !== "") {
      void this.logManager.log(
        "INFO" /* INFO */,
        "getAccessToken: Found in memory"
      );
      return this._accessToken;
    }
    if (this.accessTokenPromise) {
      void this.logManager.log(
        "INFO" /* INFO */,
        "getAccessToken: Token fetch in progress"
      );
      return this.accessTokenPromise;
    }
    this.accessTokenPromise = new Promise((resolve, reject) => {
      (async () => {
        try {
          console.log("Fetching token from local storage or remote");
          let token = localStorage.getItem("pcid_at" /* PcidAccessToken */);
          if (!token && shouldRefresh) {
            void this.logManager.log(
              "INFO" /* INFO */,
              "getAccessToken: Refreshing token"
            );
            try {
              token = await this.refreshAccessToken();
            } catch (error) {
              if (error instanceof FetchTokenResponseError)
                token = "";
              else
                throw error;
            }
          }
          this._accessToken = token || "";
          if (this._accessToken !== "") {
            void this.logManager.log(
              "INFO" /* INFO */,
              "getAccessToken: Access token returned"
            );
            localStorage.setItem("pcid_at" /* PcidAccessToken */, this._accessToken);
          }
          resolve(this._accessToken);
        } catch (error) {
          console.error("Error fetching access token", error);
          reject(error);
        } finally {
          this.accessTokenPromise = null;
        }
      })().catch((error) => {
        console.error("Error in async IIFE", error);
        reject(error);
      });
    });
    return this.accessTokenPromise;
  }
  /**
   * Retrieves the ID token from local storage.
   *
   * @returns The ID token, or null if it does not exist.
   */
  getIdToken() {
    return localStorage.getItem("pcid_it" /* PcidIdToken */);
  }
  /**
   * Retrieves the PCid from the JWT (JSON Web Token) - id token or access token.
   *
   * @returns a Promise of PCID, or null if it could not be extracted.
   */
  async getPcid() {
    void this.logManager.log("INFO" /* INFO */, "getPcid function called");
    let token = this.getIdToken();
    if (token === null || token === "") {
      token = await this.getAccessToken();
    }
    let pcid = null;
    try {
      pcid = getPcidFromJwt(token);
    } catch (e) {
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `Error while decoding jwt to get PCID: ${e.message}`
      );
    }
    void this.logManager.log("INFO" /* INFO */, "pcid returned");
    return pcid;
  }
  async getEmail() {
    void this.logManager.log("INFO" /* INFO */, "getEmail function called");
    const pcid = await this.getPcid();
    if (pcid && pcid !== "") {
      try {
        const accessToken = await this.getAccessToken();
        const getProfileUrl = `${getApiUrl(
          this.environment
        )}/v1/customers/${pcid}/profile`;
        const getProfileResponse = await fetch(getProfileUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`
          }
        });
        const response = getProfileResponse.json();
        void this.logManager.log("INFO" /* INFO */, "getEmail response returned");
        return (await response).email;
      } catch (error) {
        void this.logManager.log(
          "ERROR" /* ERROR */,
          `Error while fetching email address: ${error.message}`
        );
        throw new GetProfileResponseError(
          errorMessages.GET_PROFILE_CALL_ERROR + pcid
        );
      }
    }
    return null;
  }
  /**
   * Uses RT from http-only cookie to fetch the new access token
   * @returns  new access token (AT)
   */
  async refreshAccessToken() {
    const path = `${getApiBaseUrl()}/api/refresh`;
    const response = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        relyingParty: this.relyingParty
      })
    });
    const data = await response.json();
    if (data.error) {
      if (response.status !== 400) {
        void this.logManager.log(
          "ERROR" /* ERROR */,
          `Error while refreshing access token: ${data.error_description}`
        );
      }
      throw new FetchTokenResponseError(
        data.error_description || "Error while refreshing access token"
      );
    }
    if (!await validateToken(
      data.access_token,
      this.environment,
      this.logManager
    )) {
      throw new JwtValidationError(errorMessages.JWT_VALIDATION_FAILED);
    }
    if (data.access_token) {
      localStorage.setItem("pcid_at" /* PcidAccessToken */, data.access_token);
      this._accessToken = data.access_token;
    }
    let pcidUuid = null;
    try {
      if (data.access_token) {
        pcidUuid = getPcidFromJwt(data.access_token);
      }
      if (pcidUuid) {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("pcid_uuid" /* PcidUuid */, pcidUuid);
        }
        void this.logManager.log(
          "INFO" /* INFO */,
          `refreshAccessToken: PCid UUID extracted and stored: ${pcidUuid}`
        );
      } else {
        void this.logManager.log(
          "WARN" /* WARN */,
          "refreshAccessToken: PCid UUID could not be extracted from refreshed token"
        );
      }
    } catch (error) {
      void this.logManager.log(
        "ERROR" /* ERROR */,
        `refreshAccessToken: Error extracting PCid UUID: ${error.message}`
      );
    }
    void this.logManager.log("INFO" /* INFO */, "Access Token refreshed");
    return this._accessToken;
  }
  /**
   * revokes all tokens and clear from cookies and storage
   * @param redirectUri - The URL to redirect to after logout
   * @throws LogoutError if something goes wrong.
   */
  async logoutFunction(redirectUri) {
    try {
      void this.logManager.log("INFO" /* INFO */, "logoutFunction called");
      const logoutResponse = await fetch(`${getApiBaseUrl()}/api/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          redirectUri,
          idToken: this.getIdToken()
        })
      });
      const text = await logoutResponse.text();
      void this.logManager.log("INFO" /* INFO */, `Logout response: ${text}`);
      return JSON.parse(text);
    } catch (error) {
      void this.logManager.log(
        "WARN" /* WARN */,
        `Logout response: ${error.message}`
      );
      return {
        redirectUri,
        status: {
          USER_LOGOUT: "FAIL",
          REVOKE_KMSI: "UNKNOWN",
          REVOKE_REFRESH_TOKEN: "FAIL"
        },
        message: error.message
      };
    } finally {
      localStorage.removeItem("pcid_at" /* PcidAccessToken */);
      localStorage.removeItem("pcid_it" /* PcidIdToken */);
      localStorage.removeItem("pcid_uuid" /* PcidUuid */);
      await fetch(`${getApiBaseUrl()}/api/remove-refresh-token`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      window.location.replace(
        `${getBaseUrl(this.environment)}/kmsilogout?redirectUrl=${redirectUri}`
      );
    }
  }
  /**
   * revokes all session tokens and clear from cookies and storage except KMSI token
   * @param redirectUri - The URL to redirect to after logout
   * @throws LogoutError if something goes wrong.
   */
  async implicitLogoutFunction(redirectUri) {
    try {
      void this.logManager.log("INFO" /* INFO */, "implicitLogoutFunction called");
      const logoutResponse = await fetch(`${getApiBaseUrl()}/api/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          redirectUri,
          idToken: this.getIdToken()
        })
      });
      const text = await logoutResponse.text();
      void this.logManager.log(
        "INFO" /* INFO */,
        `implicitLogout response: ${text}`
      );
      return JSON.parse(text);
    } catch (error) {
      void this.logManager.log(
        "WARN" /* WARN */,
        `implicitLogout error: ${error.message}`
      );
      return {
        redirectUri,
        status: {
          USER_LOGOUT: "FAIL",
          REVOKE_KMSI: "FAIL",
          REVOKE_REFRESH_TOKEN: "FAIL"
        },
        message: error.message
      };
    } finally {
      localStorage.removeItem("pcid_at" /* PcidAccessToken */);
      localStorage.removeItem("pcid_it" /* PcidIdToken */);
      localStorage.removeItem("pcid_uuid" /* PcidUuid */);
      window.location.replace(redirectUri);
    }
  }
};

export {
  SessionImpl
};
